import { checkPimsPermission } from "@helpers/checkPermission";
import { DEFAULT_PATH, isValidPimsPath } from "./pimsData";

/** localStorage key 前綴：`pimsDefaultPath:${userId}` */
export const PIMS_DEFAULT_PATH_STORAGE_KEY_PREFIX = "pimsDefaultPath";

/** 舊版全站共用 key，讀取時會遷移到依帳號 key */
export const LEGACY_PIMS_DEFAULT_PATH_STORAGE_KEY = "pimsDefaultPath";

/** @deprecated 請改用 getPimsDefaultPathStorageKey */
export const PIMS_DEFAULT_PATH_STORAGE_KEY = LEGACY_PIMS_DEFAULT_PATH_STORAGE_KEY;

const REVX_USER_ID_STORAGE_KEY = "revxId";

/** 目前登入帳號 id（與登入流程寫入的 revxId 一致） */
export function getCurrentPimsUserId() {
  if (typeof window === "undefined") return null;
  const id = localStorage.getItem(REVX_USER_ID_STORAGE_KEY);
  return id != null && id !== "" ? String(id) : null;
}

export function getPimsDefaultPathStorageKey(userId = getCurrentPimsUserId()) {
  if (userId == null || userId === "") return null;
  return `${PIMS_DEFAULT_PATH_STORAGE_KEY_PREFIX}:${String(userId)}`;
}

function readSavedPimsDefaultPath(userId = getCurrentPimsUserId()) {
  if (typeof window === "undefined") return null;

  const key = getPimsDefaultPathStorageKey(userId);
  if (key) {
    const scoped = localStorage.getItem(key);
    if (scoped) return scoped;
  }

  // 舊資料遷移：僅在能對應到目前帳號時寫入專屬 key
  const legacy = localStorage.getItem(LEGACY_PIMS_DEFAULT_PATH_STORAGE_KEY);
  if (legacy && key) {
    localStorage.setItem(key, legacy);
    localStorage.removeItem(LEGACY_PIMS_DEFAULT_PATH_STORAGE_KEY);
    return legacy;
  }

  return null;
}

const PIMS_PATH_PRIORITY = [
  "/pims/vendors",
  "/pims/inventoryList",
  "/pims/itemPrices",
  "/pims/inventoryRecords",
  "/pims/stocktakes",
  "/pims/purchaseRequestsList",
];

/** 依權限回傳第一個可進入的請採驗子頁 */
export function getPimsFirstAvailablePath(permissions) {
  if (!permissions || typeof permissions !== "object") {
    return DEFAULT_PATH;
  }
  for (const path of PIMS_PATH_PRIORITY) {
    if (checkPimsPermission(path, permissions)) {
      return path;
    }
  }
  return DEFAULT_PATH;
}

export function persistPimsDefaultPath(path, userId = getCurrentPimsUserId()) {
  if (typeof window === "undefined" || !path) return;
  const key = getPimsDefaultPathStorageKey(userId);
  if (!key) return;
  localStorage.setItem(key, path);
  localStorage.removeItem(LEGACY_PIMS_DEFAULT_PATH_STORAGE_KEY);
}

/** 清除指定帳號的預設頁籤；未傳 userId 時清除目前登入帳號 */
export function clearPimsDefaultPath(userId = getCurrentPimsUserId()) {
  if (typeof window === "undefined") return;
  const key = getPimsDefaultPathStorageKey(userId);
  if (key) localStorage.removeItem(key);
}

/** 清除舊版全站 key（登出時可呼叫，避免殘留） */
export function clearLegacyPimsDefaultPath() {
  if (typeof window !== "undefined") {
    localStorage.removeItem(LEGACY_PIMS_DEFAULT_PATH_STORAGE_KEY);
  }
}

/**
 * 解析請採驗預設頁籤：可用則沿用 localStorage；否則導向第一個可進子頁並重寫 localStorage。
 * @returns {{ path: string, didReset: boolean }} didReset 表示已將「設定預設頁籤」重設為 path
 */
export function resolvePimsDefaultPath(
  permissions,
  userId = getCurrentPimsUserId()
) {
  const fallback = getPimsFirstAvailablePath(permissions);

  if (typeof window === "undefined") {
    return { path: fallback, didReset: false };
  }

  const savedPath = readSavedPimsDefaultPath(userId);
  const savedUsable =
    savedPath &&
    isValidPimsPath(savedPath) &&
    checkPimsPermission(savedPath, permissions);

  if (savedUsable) {
    return { path: savedPath, didReset: false };
  }

  if (savedPath) {
    persistPimsDefaultPath(fallback, userId);
    return { path: fallback, didReset: true };
  }

  return { path: fallback, didReset: false };
}

/** 讀取並同步預設頁籤（無權限時會自動重設 localStorage） */
export function getPimsDefaultPath(
  permissions,
  userId = getCurrentPimsUserId()
) {
  return resolvePimsDefaultPath(permissions, userId).path;
}

export function getSavedPimsDefaultPath(userId = getCurrentPimsUserId()) {
  return readSavedPimsDefaultPath(userId);
}
